import { RuleViolationError } from '../errors.js';
import type { MessageKind } from './attachments.js';
import type { ConversationStatus } from './stateMachine.js';

export const REACTION_EMOJI = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;
export type ReactionEmoji = (typeof REACTION_EMOJI)[number];

// Who reacted travels with the summary; whether that includes the viewer is
// the client's to decide, since one hydrated message fans out to everyone.
export interface ReactionSummary {
  emoji: ReactionEmoji;
  count: number;
  personIds: string[];
}

export function isReactionEmoji(value: string): value is ReactionEmoji {
  return (REACTION_EMOJI as readonly string[]).includes(value);
}

export function assertReactionEmoji(value: string): asserts value is ReactionEmoji {
  if (!isReactionEmoji(value)) {
    throw new RuleViolationError('chat.bad_reaction', 'That reaction is not available', {
      allowed: [...REACTION_EMOJI],
    });
  }
}

export function assertReactable(
  message: { kind: MessageKind; redactedAt: Date | null },
  conversationStatus: ConversationStatus,
): void {
  if (conversationStatus !== 'active') {
    throw new RuleViolationError('chat.conversation_archived', 'This group is archived');
  }
  if (message.kind === 'system') {
    throw new RuleViolationError('chat.react_system', 'System messages cannot be reacted to');
  }
  if (message.redactedAt !== null) {
    throw new RuleViolationError('chat.react_redacted', 'This message was unsent');
  }
}

/**
 * One summary per emoji that still has a reaction, in palette order, so every
 * client renders the same chips without sorting.
 */
export function aggregateReactions(
  rows: ReadonlyArray<{ emoji: string; personId: string; removedAt: Date | null }>,
): ReactionSummary[] {
  const byEmoji = new Map<ReactionEmoji, Set<string>>();
  for (const row of rows) {
    if (row.removedAt !== null || !isReactionEmoji(row.emoji)) continue;
    const people = byEmoji.get(row.emoji) ?? new Set<string>();
    people.add(row.personId);
    byEmoji.set(row.emoji, people);
  }
  const out: ReactionSummary[] = [];
  for (const emoji of REACTION_EMOJI) {
    const people = byEmoji.get(emoji);
    if (!people || people.size === 0) continue;
    const personIds = [...people].sort();
    out.push({ emoji, count: personIds.length, personIds });
  }
  return out;
}
